import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { LayerType } from "../../../../../constant/LayerType";
import ViewOrderItemInrixVolumeVectorTileLayer from "./layers/VectorTile/ViewOrderItemInrixVolumeVectorTileLayer";
import SelectedOrderItemInrixVolumeLayer from "./layers/SelectedOrderItemInrixVolumeLayer";
import { useViewContext } from "../../../../../context/View/ViewContext";
import { useViewOrderItemService } from "../../../../../hooks";
import { OrderItemInrixVolumesViewTools } from "./controls";
import OrderItemInrixVolumeMapClick from "./interactions/OrderItemInrixVolumeMapClick";
import OrderItemInrixVolumesMessages from "./messages/OrderItemInrixVolumesMessages";
import type {
  Envelope2d,
  InrixVolumesViewOrderItemSummary,
} from "../../../../../types/ViewOrderItem/InrixVolumes/InrixVolumesViewOrderItemSummary";

const ORDER_ITEM_INRIX_VOLUMES_MAX_ZOOM = 18;

const hasValidEnvelope = (
  envelope: Envelope2d | null | undefined,
): envelope is Envelope2d =>
  envelope !== null &&
  envelope !== undefined &&
  Number.isFinite(envelope.minX) &&
  Number.isFinite(envelope.maxX) &&
  Number.isFinite(envelope.minY) &&
  Number.isFinite(envelope.maxY);

const OrderItemInrixVolumesMapView = () => {
  const { pathname } = useLocation();
  const { view } = useViewContext();
  const viewOrderItemService = useViewOrderItemService();

  const [summary, setSummary] =
    useState<InrixVolumesViewOrderItemSummary | null>(null);

  const isOrderItemRoute = pathname
    .toLowerCase()
    .startsWith("/view/orderitem/");

  const { layer, orderItemId } = useParams();
  const decodedLayer = layer ? decodeURIComponent(layer) : "";

  const isInrixVolumesLayer =
    decodedLayer.toLowerCase() === LayerType.INRIX_VOLUMES.toLowerCase();

  const visible = isOrderItemRoute && isInrixVolumesLayer && !!orderItemId;

  useEffect(() => {
    if (!visible || !orderItemId) {
      setSummary(null);
      return;
    }

    viewOrderItemService
      .GetSummary(orderItemId, decodedLayer)
      .then((data) => {
        const summaryData = data as InrixVolumesViewOrderItemSummary;
        setSummary(summaryData);
        if (hasValidEnvelope(summaryData?.envelope)) {
          const { envelope } = summaryData;
          const extent = [
            envelope.minX,
            envelope.minY,
            envelope.maxX,
            envelope.maxY,
          ];
          view.fit(extent, {
            padding: [100, 100, 100, 100],
            duration: 500,
          });
        }
      })
      .catch(() => {});
  }, [decodedLayer, orderItemId, view, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const previousMaxZoom = view.maxZoom;
    if (previousMaxZoom < ORDER_ITEM_INRIX_VOLUMES_MAX_ZOOM) {
      view.maxZoom = ORDER_ITEM_INRIX_VOLUMES_MAX_ZOOM;
    }

    return () => {
      view.maxZoom = previousMaxZoom;
    };
  }, [view, visible]);

  return (
    <>
      <ViewOrderItemInrixVolumeVectorTileLayer
        visible={visible}
        summary={summary}
      />
      <SelectedOrderItemInrixVolumeLayer visible={visible} />
      <OrderItemInrixVolumeMapClick active={visible} summary={summary} />
      {visible && <OrderItemInrixVolumesViewTools summary={summary} />}
      {visible && <OrderItemInrixVolumesMessages />}
    </>
  );
};

export default OrderItemInrixVolumesMapView;
